import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useBetSlip, slipId } from '../context/BetSlipContext';
import {
  Runner,
  SEX_LABELS,
  formatOdds,
  formatCareer,
  formatPct,
  formatWeight,
  asText,
} from './raceFormat';

// Minimal slice of /api/horse/:name that the expandable form dropdown renders.
interface PastRun {
  date: string | null;
  venue: string;
  raceClass: string;
  distance: number;
  trackCondition: string;
  position: number | null;
  starters: number;
  margin: number | null;
  barrier: number;
  weight: number | null;
  jockey: string;
  sp: number | null;
  openingSp: number | null;
  daysSincePrev: number | null;
  spell: boolean;
  letup: boolean;
}

interface RunnerForm {
  hasProfile?: boolean;
  raceHistory: PastRun[];
}

export interface RunnerMarket {
  marketId?: string;
  status?: string;
  placeMarketId?: string;
  placeStatus?: string;
  marketName?: string;
  meetingName?: string;
  raceNumber?: number;
  date?: string;
}

interface RunnerRowProps {
  runner: Runner;
  horseLink: (name: string) => string;
  market?: RunnerMarket;
  expandAll?: boolean;
  placeRunner?: any;
}

const NUM_COLS = 11; // keep in sync with the <thead> in RaceDetail

const formatRunDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(2)}`;
};

const RunnerRow: React.FC<RunnerRowProps> = ({ runner, horseLink, market, expandAll, placeRunner }) => {
  const { addItem, hasItem,addMultiple,multipleItems } = useBetSlip();
  const [openLocal, setOpenLocal] = useState(false);
  const [form, setForm] = useState<RunnerForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const expanded = expandAll || openLocal;

  const name = asText(runner.horse_name, 'Unknown');
  const scratched = !!runner.scratched;

  // Real betting is the Betfair exchange best Back / Lay.
  const backPrice = runner.bf_back ?? 0;
  const layPrice = runner.bf_lay ?? 0;
  const tradable = !!market?.marketId && !!runner.bf_selection_id && !scratched && market?.status === 'OPEN' && runner.bf_status !== 'REMOVED';
  const canBack = tradable && backPrice > 0;
  const canLay = tradable && layPrice > 0;
  const placeTradable = !!market?.placeMarketId && market.placeStatus === 'OPEN' && !!placeRunner?.selection_id && placeRunner.status !== 'REMOVED' && !scratched;
  const backInSlip =
    tradable && hasItem(slipId(market!.marketId!, runner.bf_selection_id!, 'BACK'));
  const layInSlip =
    tradable && hasItem(slipId(market!.marketId!, runner.bf_selection_id!, 'LAY'));

  const fmtSize = (v: number | null | undefined) => {
    if (v == null) return '';
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${Math.round(v)}`;
  };

  const loadForm = async () => {
    if (fetched || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/horse/${encodeURIComponent(runner.horse_name)}`
      );
      setForm(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Form guide unavailable for this runner.');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  // When "Show all form" is toggled on, eagerly load each row's form.
  useEffect(() => {
    if (expandAll) loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll]);

  const toggle = () => {
    const next = !openLocal;
    setOpenLocal(next);
    if (next) loadForm();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  const addSide = (side: 'BACK' | 'LAY', price: number, type: 'WIN' | 'PLACE' = 'WIN') => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!market?.marketId || !runner.bf_selection_id || price <= 0) return;
    addItem({
      marketId: type === 'PLACE' ? market.placeMarketId! : market.marketId,
      winMarketId: market.marketId, marketType: type, number:runner.horse_number, stake:0,
      selectionId: runner.bf_selection_id,
      runnerName: name,
      side,
      price,
      marketName: type === 'PLACE' ? 'PLACE' : (market.marketName || 'WIN'),
      meetingName: market.meetingName,
      raceNumber: market.raceNumber,
      date: market.date,
    });
  };

  const ageSex = [runner.pf_age, runner.pf_sex && (SEX_LABELS[runner.pf_sex] || runner.pf_sex)]
    .filter(Boolean)
    .join(' ');

  // Carried to the horse page so it can show the same basic runner/market data
  // for runners with no PuntingForm profile (harness/greyhound).
  const runnerState = {
    horse_number: runner.horse_number,
    jockey: asText(runner.jockey, ''),
    trainer: asText(runner.trainer, ''),
    barrier: runner.barrier,
    weight: runner.weight,
    sp_price: runner.sp_price,
    fixed_odd_win: runner.fixed_odd_win,
    fixed_odd_place: runner.fixed_odd_place,
    tote_win: runner.tote_win,
    tote_place: runner.tote_place,
    bf_back: runner.bf_back,
    bf_lay: runner.bf_lay,
    bf_last_traded: runner.bf_last_traded,
    bf_bsp: runner.bf_bsp,
    meetingName: market?.meetingName,
    raceNumber: market?.raceNumber,
  };

  return (
    <>
      <tr
        className={`tr-runner${scratched ? ' scratched' : ''}${expanded ? ' expanded' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKeyDown}
        onMouseEnter={loadForm}
      >
        <td className="td-no">
          <div className="no-box">
            <span className="no-val">{runner.horse_number}</span>
            <span className="no-chev">▾</span>
          </div>
        </td>

        <td className="td-runner">
          <span className="silk" aria-hidden="true" />
          <div className="runner-id">
            <div className="runner-name-row">
              <Link
                to={horseLink(runner.horse_name)}
                state={{ runner: runnerState }}
                className="runner-name"
                onClick={(e) => e.stopPropagation()}
              >
                {name}
              </Link>
              {runner.barrier ? <span className="runner-barrier">({runner.barrier})</span> : null}
              {ageSex && <span className="runner-agesex">{ageSex}</span>}
              {runner.core_rank ? <span className={'mobile-runner-core core-badge core-badge-' + runner.core_rank}>C{runner.core_rank}</span> : null}
              {scratched && <span className="runner-scratched-tag">SCR</span>}
            </div>
            <div className="runner-conn">
              <span><b>J</b> {asText(runner.jockey, '—')}</span>
              <span><b>T</b> {asText(runner.trainer, '—')}</span>
            </div>
            <div className="runner-multiple-picks" aria-label={'Add '+name+' to multiples'} onClick={e=>e.stopPropagation()} onKeyDown={e=>e.stopPropagation()}>
              {(['WIN','PLACE','EW'] as const).map(type=><button type="button" key={type} disabled={!tradable || (type!=='WIN' && !placeTradable)}
                aria-pressed={multipleItems.some(x=>x.win_market_id===market?.marketId&&x.selection_id===runner.bf_selection_id&&x.type===type)}
                title={'Add '+name+' as '+(type==='EW'?'Each Way':type)+' to multiples'}
                onClick={()=>addMultiple({win_market_id:market!.marketId!,selection_id:runner.bf_selection_id!,runner:name,
                  number:runner.horse_number,track:market?.meetingName,race:market?.raceNumber,type})}>{type==='WIN'?'W':type==='PLACE'?'P':'EW'}</button>)}
            </div>
          </div>
        </td>

        <td className="td-form">{runner.pf_form || '–'}</td>
        <td className="td-weight">{runner.weight ? formatWeight(runner.weight) : '–'}</td>
        <td className="td-core-rank">{runner.core_rank ? <span className={'core-badge core-badge-' + runner.core_rank}>Core {runner.core_rank}</span> : '—'}</td>
        <td className="td-core-price">{formatOdds(runner.core_rated_price)}</td>
        <td className="td-core-price" title={runner.core_market_price_source || 'No recorded market price'}>{formatOdds(runner.core_market_price)}{runner.core_market_price_source && <small>{runner.core_market_price_source}</small>}</td>

        <td className="td-bf td-bf-back td-group-start">
          {canBack ? (
            <button
              type="button"
              className={`bf-btn bf-back${backInSlip ? ' in-slip' : ''}`}
              onClick={addSide('BACK', backPrice)}
              title={backInSlip ? 'Backing in your slip — tap to remove' : 'Back this runner'}
            >
              <span className="bf-price">{formatOdds(backPrice)}</span>
              <span className="bf-size">{fmtSize(runner.bf_back_size)}</span>
            </button>
          ) : (
            <span className="bf-empty">—</span>
          )}
        </td>
        <td className="td-bf td-bf-lay">
          {canLay ? (
            <button
              type="button"
              className={`bf-btn bf-lay${layInSlip ? ' in-slip' : ''}`}
              onClick={addSide('LAY', layPrice)}
              title={layInSlip ? 'Laying in your slip — tap to remove' : 'Lay this runner'}
            >
              <span className="bf-price">{formatOdds(layPrice)}</span>
              <span className="bf-size">{fmtSize(runner.bf_lay_size)}</span>
            </button>
          ) : (
            <span className="bf-empty">—</span>
          )}
        </td>
        {(['BACK','LAY'] as const).map(side=>{
          const price=side==='BACK'?placeRunner?.best_back:placeRunner?.best_lay;
          const size=side==='BACK'?placeRunner?.best_back_size:placeRunner?.best_lay_size;
          const selected=!!market?.placeMarketId&&hasItem(slipId(market.placeMarketId,placeRunner?.selection_id,side));
          return <td key={side} className="td-bf">
            {placeTradable&&price>1?<button type="button" className={`bf-btn ${side==='BACK'?'bf-back':'bf-lay'}${selected?' in-slip':''}`} onClick={addSide(side,price,'PLACE')} title={`${side} ${name} PLACE`}>
              <span className="bf-price">{formatOdds(price)}</span><span className="bf-size">{fmtSize(size)}</span>
            </button>:<span className="bf-empty" title="Place market unavailable or closed">{formatOdds(placeRunner?.bsp)}</span>}
          </td>;
        })}
      </tr>

      {expanded && (
        <tr className="tr-detail">
          <td colSpan={NUM_COLS}>
            <div className="runner-detail">
              <div className="runner-stats">
                <div className="stat"><label>Career</label><span>{formatCareer(runner.pf_career)}</span></div>
                <div className="stat"><label>Win%</label><span>{formatPct(runner.pf_win_pct)}</span></div>
                <div className="stat"><label>Place%</label><span>{formatPct(runner.pf_place_pct)}</span></div>
                <div className="stat"><label>SP</label><span>{formatOdds(runner.sp_price)}</span></div>
                <div className="stat"><label>BF Back</label><span>{formatOdds(runner.bf_back)}</span></div>
                <div className="stat"><label>BF Lay</label><span>{formatOdds(runner.bf_lay)}</span></div>
                <div className="stat"><label>Win open / 10am / last 30m</label><span>{formatOdds(runner.bf_open)} / {formatOdds(runner.bf_ten_am)} / {formatOdds(runner.bf_last30)}</span></div>
                <div className="stat"><label>Fixed win / place</label><span>{formatOdds(runner.fixed_odd_win)} / {formatOdds(runner.fixed_odd_place)}</span></div>
              </div>

              {loading && <div className="runner-form-loading">Loading form…</div>}
              {!loading && error && <div className="runner-form-empty">{error}</div>}

              {!loading && !error && form && form.hasProfile === false && (
                <div className="runner-basic">
                  <p className="runner-basic-note">
                    No PuntingForm form guide for this runner (harness &amp; greyhound aren’t covered).
                    Showing the runner &amp; market data we have:
                  </p>
                  <div className="runner-stats">
                    <div className="stat"><label>No.</label><span>{runner.horse_number}</span></div>
                    <div className="stat"><label>Jockey/Driver</label><span>{asText(runner.jockey, '—')}</span></div>
                    <div className="stat"><label>Trainer</label><span>{asText(runner.trainer, '—')}</span></div>
                    <div className="stat"><label>Barrier</label><span>{runner.barrier || '—'}</span></div>
                    <div className="stat"><label>Weight</label><span>{runner.weight ? formatWeight(runner.weight) : '—'}</span></div>
                    <div className="stat"><label>Fixed Win</label><span>{formatOdds(runner.fixed_odd_win)}</span></div>
                    <div className="stat"><label>Fixed Place</label><span>{formatOdds(runner.fixed_odd_place)}</span></div>
                    <div className="stat"><label>Tote Win</label><span>{formatOdds(runner.tote_win)}</span></div>
                    <div className="stat"><label>Tote Place</label><span>{formatOdds(runner.tote_place)}</span></div>
                    <div className="stat"><label>BF LTP</label><span>{formatOdds(runner.bf_last_traded)}</span></div>
                    <div className="stat"><label>BF BSP</label><span>{formatOdds(runner.bf_bsp)}</span></div>
                  </div>
                </div>
              )}

              {!loading && !error && form && form.hasProfile !== false && (
                form.raceHistory && form.raceHistory.length > 0 ? (
                  <div className="runner-form-table-wrap">
                    <table className="runner-form-table">
                      <thead>
                        <tr>
                          <th>Fin</th><th>Date</th><th title="Days since previous run">Days</th><th>Track</th><th>Dist</th><th>Cond</th>
                          <th>Class</th><th>Jockey</th><th>Wgt</th><th>Brr</th><th>Mgn</th><th title="Opening price">Open</th><th title="Starting price">SP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.raceHistory.slice(0, 6).map((run, i) => (
                          <React.Fragment key={i}>
                          <tr className={run.position === 1 ? 'win' : ''}>
                            <td>
                              {run.position ? (
                                <span className={`fin-badge fin-${run.position <= 3 ? run.position : 'x'}`}>
                                  {run.position}{run.starters ? `/${run.starters}` : ''}
                                </span>
                              ) : '—'}
                            </td>
                            <td>{formatRunDate(run.date)}</td>
                            <td title={run.daysSincePrev != null ? `${run.daysSincePrev} days since previous run` : ''}>
                              {run.daysSincePrev != null ? `${run.daysSincePrev}d` : '—'}
                            </td>
                            <td>{run.venue || '—'}</td>
                            <td>{run.distance ? `${run.distance}m` : '—'}</td>
                            <td>{run.trackCondition || '—'}</td>
                            <td>{run.raceClass || '—'}</td>
                            <td>{run.jockey || '—'}</td>
                            <td>{run.weight ? `${run.weight}kg` : '—'}</td>
                            <td>{run.barrier || '—'}</td>
                            <td>{run.margin != null ? `${run.margin}L` : '—'}</td>
                            <td>{run.openingSp != null ? `$${run.openingSp.toFixed(2)}` : '—'}</td>
                            <td>{run.sp != null ? `$${run.sp.toFixed(2)}` : '—'}</td>
                          </tr>
                          {(run.spell || run.letup) && run.daysSincePrev != null && (
                            <tr className="form-spell-row">
                              <td colSpan={13}>
                                {run.spell ? '⟢ Spell' : '⟢ Let-up'} — {run.daysSincePrev} days
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="runner-form-empty">No past-run history available.</div>
                )
              )}

              <div className="runner-detail-footer">
                <Link to={horseLink(runner.horse_name)} state={{ runner: runnerState }} className="runner-fullform-link">
                  View full form guide →
                </Link>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default RunnerRow;

